import React, { useEffect, useState } from 'react';
import { Truck } from 'lucide-react';
import { getDocuments } from '../../services/firebase';

interface Vehicle {
  documentId?: string;
  id?: string;           // Some docs may use `vehicleId`; we’ll display both
  vehicleId?: string;    // Custom ID like "VEH-002"
  vehicleNumber?: string;
  type?: string;
  capacity?: number;
  kilometers?: number;
  isActive?: boolean;
  warehouseId?: string;
}

const VehicleSection: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const docs = await getDocuments('vehicles');
      setVehicles(docs as Vehicle[]);
    } catch (e) {
      console.error('Failed to load vehicles', e);
      setError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Truck className="w-6 h-6 text-gray-700" />
          <h1 className="text-xl font-semibold text-gray-800">Vehicle</h1>
        </div>
        <button onClick={load} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50">Refresh</button>
      </div>

      {error && <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacity</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kilometers</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading vehicles…</td>
                </tr>
              ) : vehicles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No vehicles found</td>
                </tr>
              ) : (
                vehicles.map((v) => (
                  <tr key={v.documentId || v.vehicleId || v.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">{v.vehicleId || v.id || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{v.vehicleNumber || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{v.type || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{typeof v.capacity === 'number' ? v.capacity : '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{typeof v.kilometers === 'number' ? v.kilometers : '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs ${v.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {v.isActive ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{v.warehouseId || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VehicleSection;
